import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, DollarSign, MessageSquare, CheckCircle, Send, ClipboardList, Calendar, X, CalendarCheck, Image, AlertTriangle } from 'lucide-react';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { QuoteRow, QuoteConversationMessage } from '../types';
import { getCustomerQuoteDisplayStatus } from '../utils/quoteDisplayStatus';
import toast from 'react-hot-toast';

const formatRequestNumber = (id: string) => (id ? `Q-${id.slice(0, 8)}` : '—');
const formatSchedule = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};
const formatDate = (v: string) => {
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};
const formatMoney = (n: number | null | undefined) =>
  n != null && Number.isFinite(n) ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n) : '—';

const PENDING_TOOLTIP_TEXT = 'You will receive an email when the business sends an offer. You can also return to this page anytime to check for updates.';
const OFFER_ACCEPTED_TOOLTIP_TEXT = 'The business will schedule your appointment. You can contact them directly using the conversation below to coordinate timing and any other arrangements.';
const OFFER_SENT_TOOLTIP_TEXT = 'The business has sent you a quote offer. Review the offer amount and details. If you would like to proceed, please accept the offer. You can also reply in the conversation to ask questions or request changes before accepting.';
const OFFER_EXPIRED_TOOLTIP_TEXT = 'This offer has expired. Please message or call the business to request a new offer.';
const CONVERSATION_TOOLTIP_TEXT = 'Use the Conversation section on this page to message the business with any questions or additional details. They may use this to clarify your request before sending an offer.';
const BUSINESS_OFFER_TOOLTIP_TEXT = 'Accepting the offer confirms that you want the business to move forward with scheduling. If a deposit is required, it will be due before the appointment is confirmed.';

const CustomerQuoteRequestDetailsPage: React.FC = () => {
  const { quoteId } = useParams<{ quoteId: string }>();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [quote, setQuote] = useState<QuoteRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [showAcceptConfirm, setShowAcceptConfirm] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [conversationInput, setConversationInput] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [appointmentDetails, setAppointmentDetails] = useState<{ scheduledAt: string | null; status?: string | null } | null>(null);
  const [presignedImageUrls, setPresignedImageUrls] = useState<string[] | null>(null);
  const [presignedImageUrlsLoading, setPresignedImageUrlsLoading] = useState(false);
  const [loadedImageIndices, setLoadedImageIndices] = useState<Set<number>>(new Set());
  const [lightboxImageUrl, setLightboxImageUrl] = useState<string | null>(null);
  const [pendingInfoTooltipOpen, setPendingInfoTooltipOpen] = useState(false);
  const pendingInfoTooltipTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [offerAcceptedInfoTooltipOpen, setOfferAcceptedInfoTooltipOpen] = useState(false);
  const offerAcceptedInfoTooltipTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [offerSentInfoTooltipOpen, setOfferSentInfoTooltipOpen] = useState(false);
  const offerSentInfoTooltipTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [businessOfferInfoTooltipOpen, setBusinessOfferInfoTooltipOpen] = useState(false);
  const businessOfferInfoTooltipTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [conversationInfoTooltipOpen, setConversationInfoTooltipOpen] = useState(false);
  const conversationInfoTooltipTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadQuote = useCallback(async () => {
    if (!quoteId) return;
    try {
      setLoading(true);
      const data = await apiService.getQuote(quoteId);
      setQuote(data);
    } catch (err) {
      console.error('Error loading quote:', err);
      toast.error('Failed to load quote request');
      setQuote(null);
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  useEffect(() => {
    loadQuote();
  }, [loadQuote]);

  useEffect(() => {
    if (!quote?.appointmentId) {
      setAppointmentDetails(null);
      return;
    }
    apiService
      .getAppointment(quote.appointmentId)
      .then((apt) => {
        if (!apt) {
          setAppointmentDetails(null);
          return;
        }
        setAppointmentDetails({
          scheduledAt: apt.scheduledAt ?? null,
          status: apt.status ?? null,
        });
      })
      .catch(() => setAppointmentDetails(null));
  }, [quote?.appointmentId]);

  useEffect(() => {
    if (!quote?.id || !quote.customerUploadedImages?.length) {
      setPresignedImageUrls(null);
      setPresignedImageUrlsLoading(false);
      return;
    }
    let cancelled = false;
    setPresignedImageUrlsLoading(true);
    apiService
      .getQuotePresignedImageUrls(quote.id)
      .then(({ urls: signed }) => {
        if (!cancelled) {
          setPresignedImageUrls(signed);
          setLoadedImageIndices(new Set());
        }
      })
      .catch((err) => {
        console.error('Failed to load quote image URLs:', err);
        if (!cancelled) setPresignedImageUrls(null);
      })
      .finally(() => {
        if (!cancelled) setPresignedImageUrlsLoading(false);
      });
    return () => { cancelled = true; };
  }, [quote?.id, quote?.customerUploadedImages?.length]);

  const goBack = () => navigate('/dashboard/customer/quote-requests');
  const isOfferExpired =
    quote?.offerExpiredAt != null && !Number.isNaN(new Date(quote.offerExpiredAt).getTime())
      ? new Date(quote.offerExpiredAt).getTime() <= Date.now()
      : false;

  const quoteDisplayStatus = quote
    ? getCustomerQuoteDisplayStatus({
        ...quote,
        appointmentStatus: appointmentDetails?.status ?? quote.appointmentStatus ?? null,
      })
    : null;

  const isLinkedAppointmentCancelled =
    Boolean(quote?.appointmentId) &&
    String(appointmentDetails?.status ?? quote?.appointmentStatus ?? '').toUpperCase() === 'CANCELLED';

  const handleAcceptOffer = async () => {
    if (!quoteId || !quote || accepting) return;
    setAccepting(true);
    try {
      const data = await apiService.acceptQuote(quoteId);
      setQuote((prev) => prev ? { ...prev, status: data.status, offerExpiredAt: data.offerExpiredAt ?? prev.offerExpiredAt, conversations: data.conversations ?? prev.conversations } : null);
      setShowAcceptConfirm(false);
      toast.success('Offer accepted');
    } catch (err) {
      console.error('Error accepting offer:', err);
      toast.error((err as any)?.response?.data?.message || 'Failed to accept offer');
    } finally {
      setAccepting(false);
    }
  };

  const handleSendMessage = async () => {
    const text = conversationInput.trim();
    if (!quoteId || !text || sendingMessage) return;
    try {
      setSendingMessage(true);
      const result = await apiService.updateQuote(quoteId, { newMessage: { text } });
      setQuote((prev) => (prev ? { ...prev, conversations: result.conversations ?? prev.conversations } : null));
      setConversationInput('');
      toast.success('Message sent');
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <CustomerNavbar
        onLogout={logout}
        showDashboardLink
        showLogoutButton
        unreadNotificationsCount={0}
        onNotificationsClick={() => navigate('/dashboard/customer/notifications')}
      />
      <div className="flex-1 pt-6 pb-12">
        <div className="px-4 lg:px-8 max-w-7xl mx-auto">
          <button
            onClick={goBack}
            className="flex items-center font-semibold mb-4"
            style={{ color: '#0B4778' }}
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Quote Requests
          </button>
          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : !quote ? (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center text-gray-600 text-sm">
              Quote request not found.
            </div>
          ) : (
            <div className="space-y-6">
              <div className="mb-4">
                <h1 className="text-xl font-bold text-gray-900 mb-0">
                  <span className="uppercase">{formatRequestNumber(quote.id)}</span>
                  {quote.businessName && (
                    <>
                      {' : '}
                      {quote.businessShortName ? (
                        <Link
                          to={`/${quote.businessShortName}`}
                          className="text-[#0B4778] hover:underline"
                        >
                          {quote.businessName}
                        </Link>
                      ) : (
                        quote.businessName
                      )}
                    </>
                  )}
                </h1>
                <p className="text-gray-600 text-sm mb-0">Submitted {formatDate(quote.createdAt)}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className={`px-2.5 py-1 text-xs font-medium rounded-full ${quoteDisplayStatus?.badgeClass ?? 'bg-gray-100 text-gray-800'}`}>
                  {quoteDisplayStatus?.label ?? '—'}
                </span>
              </div>

              {(quote.status === 'NEW' || quote.status === 'PENDING') && (
                <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-4">
                  <div className="grid gap-x-2 gap-y-2" style={{ gridTemplateColumns: 'auto 1fr', alignItems: 'start' }}>
                    <ClipboardList className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="font-semibold text-amber-800 text-sm m-0">Quote Request Submitted</p>
                      <span
                        className="relative flex-shrink-0"
                        onMouseEnter={() => {
                          if (pendingInfoTooltipTimeoutRef.current) {
                            clearTimeout(pendingInfoTooltipTimeoutRef.current);
                            pendingInfoTooltipTimeoutRef.current = null;
                          }
                          setPendingInfoTooltipOpen(true);
                        }}
                        onMouseLeave={() => {
                          pendingInfoTooltipTimeoutRef.current = setTimeout(() => setPendingInfoTooltipOpen(false), 150);
                        }}
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-amber-100/90 text-amber-700" aria-label="More information">
                          <i className="bi bi-info-circle text-base" aria-hidden />
                        </span>
                        <span
                          className={`absolute left-1/2 top-full z-10 mt-1.5 -translate-x-1/2 whitespace-normal rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-normal text-gray-600 shadow-lg transition-opacity duration-150 ${pendingInfoTooltipOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                          style={{ width: 'max-content', maxWidth: '280px' }}
                          onMouseEnter={() => {
                            if (pendingInfoTooltipTimeoutRef.current) {
                              clearTimeout(pendingInfoTooltipTimeoutRef.current);
                              pendingInfoTooltipTimeoutRef.current = null;
                            }
                            setPendingInfoTooltipOpen(true);
                          }}
                          onMouseLeave={() => {
                            pendingInfoTooltipTimeoutRef.current = setTimeout(() => setPendingInfoTooltipOpen(false), 150);
                          }}
                        >
                          <span className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-gray-200 bg-white" />
                          {PENDING_TOOLTIP_TEXT}
                        </span>
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-gray-700" style={{ gridColumn: '1 / -1' }}>
                      <p>
                        Your quote request has been received and is currently under review by the business.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {quote.status === 'BUSINESS_OFFER_SENT' && (
                <div className={`rounded-lg p-4 ${isOfferExpired ? 'border border-amber-200 bg-amber-50/80' : 'border border-[#0B4778]/30 bg-[#0B4778]/5'}`}>
                  <div className="grid gap-x-2 gap-y-2" style={{ gridTemplateColumns: 'auto 1fr', alignItems: 'start' }}>
                    {isOfferExpired ? (
                      <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-700" />
                    ) : (
                      <DollarSign className="w-5 h-5 flex-shrink-0 mt-0.5 text-[#0B4778]" />
                    )}
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className={`font-semibold text-sm m-0 ${isOfferExpired ? 'text-amber-800' : 'text-[#0B4778]'}`}>{isOfferExpired ? 'Offer Expired' : 'You Have A New Offer'}</p>
                      <span
                        className="relative flex-shrink-0"
                        onMouseEnter={() => {
                          if (offerSentInfoTooltipTimeoutRef.current) {
                            clearTimeout(offerSentInfoTooltipTimeoutRef.current);
                            offerSentInfoTooltipTimeoutRef.current = null;
                          }
                          setOfferSentInfoTooltipOpen(true);
                        }}
                        onMouseLeave={() => {
                          offerSentInfoTooltipTimeoutRef.current = setTimeout(() => setOfferSentInfoTooltipOpen(false), 150);
                        }}
                      >
                        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${isOfferExpired ? 'bg-amber-100/90 text-amber-700' : 'bg-[#0B4778]/10 text-[#0B4778]'}`} aria-label="More information">
                          <i className="bi bi-info-circle text-base" aria-hidden />
                        </span>
                        <span
                          className={`absolute left-1/2 top-full z-10 mt-1.5 -translate-x-1/2 whitespace-normal rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-normal text-gray-600 shadow-lg transition-opacity duration-150 ${offerSentInfoTooltipOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                          style={{ width: 'max-content', maxWidth: '280px' }}
                          onMouseEnter={() => {
                            if (offerSentInfoTooltipTimeoutRef.current) {
                              clearTimeout(offerSentInfoTooltipTimeoutRef.current);
                              offerSentInfoTooltipTimeoutRef.current = null;
                            }
                            setOfferSentInfoTooltipOpen(true);
                          }}
                          onMouseLeave={() => {
                            offerSentInfoTooltipTimeoutRef.current = setTimeout(() => setOfferSentInfoTooltipOpen(false), 150);
                          }}
                        >
                          <span className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-gray-200 bg-white" />
                          {isOfferExpired ? OFFER_EXPIRED_TOOLTIP_TEXT : OFFER_SENT_TOOLTIP_TEXT}
                        </span>
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-gray-700" style={{ gridColumn: '1 / -1' }}>
                      <p>
                        {isOfferExpired
                          ? 'This offer has expired. Please message or call the business to request a new offer.'
                          : 'Review the offer amount and details. Accept the offer to proceed or reply in the conversation to ask questions or request changes before accepting.'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {isLinkedAppointmentCancelled && quote.appointmentId && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm text-gray-700">
                      <p className="font-semibold text-red-800">Appointment cancelled</p>
                      <p>
                        This appointment has been cancelled and is no longer scheduled. Your accepted quote is still active — please use the conversation below to work with the business to arrange a new appointment.
                      </p>
                      {appointmentDetails?.scheduledAt && (
                        <p className="font-medium text-gray-600">
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            Previously scheduled: {formatSchedule(appointmentDetails.scheduledAt)}
                          </span>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isLinkedAppointmentCancelled && quote.status === 'BUSINESS_OFFER_ACCEPTED' && quote.appointmentId && (
                <>
                  {quote.requiredDepositAmount != null && quote.requiredDepositAmount > 0 ? (
                    <div className="rounded-lg border border-amber-200 bg-amber-50/90 p-4">
                      <div className="flex gap-3">
                        <DollarSign className="w-5 h-5 text-amber-700 flex-shrink-0 mt-0.5" />
                        <div className="space-y-2 text-sm text-gray-700">
                          <p className="font-semibold text-amber-800">Appointment created — deposit required</p>
                          <p>
                            The business has created your appointment. To secure your booking, please pay the required deposit of {formatMoney(quote.requiredDepositAmount)}. Your appointment is not confirmed until the deposit is received.
                          </p>
                          {appointmentDetails?.scheduledAt && (
                            <p className="font-medium text-gray-900">
                              <span className="inline-flex items-center gap-1"><CalendarCheck className="w-4 h-4" /> Scheduled: {formatSchedule(appointmentDetails.scheduledAt)}</span>
                            </p>
                          )}
                          <p className="pt-1">
                            <Link
                              to={`/dashboard/customer/appointments/${quote.appointmentId}/deposit`}
                              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                              style={{ backgroundColor: '#0B4778' }}
                            >
                              Pay deposit
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                      <div className="flex gap-3">
                        <CalendarCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <div className="space-y-2 text-sm text-gray-700">
                          <p className="font-semibold text-green-800">Appointment created</p>
                          <p>
                            The business has created your appointment. For any changes (reschedule, cancellation, or questions), please work directly with the business using the conversation below or contact them through your appointment details.
                          </p>
                          {appointmentDetails?.scheduledAt && (
                            <p className="font-medium text-gray-900">
                              <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" /> Scheduled: {formatSchedule(appointmentDetails.scheduledAt)}</span>
                            </p>
                          )}
                          <p className="pt-1">
                            <Link
                              to={`/dashboard/customer/appointments/${quote.appointmentId}`}
                              className="inline-flex items-center gap-2 text-sm font-medium"
                              style={{ color: '#0B4778' }}
                            >
                              View appointment
                            </Link>
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {quote.status === 'BUSINESS_OFFER_ACCEPTED' && !quote.appointmentId && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="grid gap-x-2 gap-y-2" style={{ gridTemplateColumns: 'auto 1fr', alignItems: 'start' }}>
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="flex items-center gap-1.5 min-w-0">
                      <p className="font-semibold text-green-800 text-sm m-0">Offer Accepted</p>
                      <span
                        className="relative flex-shrink-0"
                        onMouseEnter={() => {
                          if (offerAcceptedInfoTooltipTimeoutRef.current) {
                            clearTimeout(offerAcceptedInfoTooltipTimeoutRef.current);
                            offerAcceptedInfoTooltipTimeoutRef.current = null;
                          }
                          setOfferAcceptedInfoTooltipOpen(true);
                        }}
                        onMouseLeave={() => {
                          offerAcceptedInfoTooltipTimeoutRef.current = setTimeout(() => setOfferAcceptedInfoTooltipOpen(false), 150);
                        }}
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-100/90 text-green-700" aria-label="More information">
                          <i className="bi bi-info-circle text-base" aria-hidden />
                        </span>
                        <span
                          className={`absolute left-1/2 top-full z-10 mt-1.5 -translate-x-1/2 whitespace-normal rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-normal text-gray-600 shadow-lg transition-opacity duration-150 ${offerAcceptedInfoTooltipOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                          style={{ width: 'max-content', maxWidth: '280px' }}
                          onMouseEnter={() => {
                            if (offerAcceptedInfoTooltipTimeoutRef.current) {
                              clearTimeout(offerAcceptedInfoTooltipTimeoutRef.current);
                              offerAcceptedInfoTooltipTimeoutRef.current = null;
                            }
                            setOfferAcceptedInfoTooltipOpen(true);
                          }}
                          onMouseLeave={() => {
                            offerAcceptedInfoTooltipTimeoutRef.current = setTimeout(() => setOfferAcceptedInfoTooltipOpen(false), 150);
                          }}
                        >
                          <span className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-gray-200 bg-white" />
                          {OFFER_ACCEPTED_TOOLTIP_TEXT}
                        </span>
                      </span>
                    </div>
                    <div className="space-y-2 text-sm text-gray-700" style={{ gridColumn: '1 / -1' }}>
                      <p>
                        The business will schedule your appointment. You can contact them directly using the conversation below to coordinate timing and any other arrangements.
                      </p>
                      {quote.requiredDepositAmount != null && quote.requiredDepositAmount > 0 && (
                        <p>
                          A deposit of {formatMoney(quote.requiredDepositAmount)} is required. Payment must be submitted when the appointment is scheduled and before services are rendered.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isLinkedAppointmentCancelled && quote.status === 'DEPOSIT_RECEIVED' && (
                <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                  <div className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm text-gray-700">
                      <p className="font-semibold text-green-800">You&apos;re all set</p>
                      <p>
                        Your deposit has been received and your appointment is confirmed. We look forward to seeing you. If you need to reschedule or have any questions, reach out to the business using the conversation below or from your appointment details.
                      </p>
                      {appointmentDetails?.scheduledAt && (
                        <p className="font-medium text-gray-900 pt-1">
                          <span className="inline-flex items-center gap-1"><CalendarCheck className="w-4 h-4" /> Appointment: {formatSchedule(appointmentDetails.scheduledAt)}</span>
                        </p>
                      )}
                      {quote.appointmentId && (
                        <p className="pt-1">
                          <Link
                            to={`/dashboard/customer/appointments/${quote.appointmentId}`}
                            className="inline-flex items-center gap-2 text-sm font-medium"
                            style={{ color: '#0B4778' }}
                          >
                            View appointment
                          </Link>
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
                {/* Left column */}
                <div className="space-y-6">
                  {/* Services */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <ClipboardList className="w-4 h-4" />
                    Services requested
                  </h2>
                    <ul className="space-y-2">
                      {(quote.services || []).map((s) => (
                        <li key={s.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                          <span className="text-sm font-medium text-gray-900">{s.name || s.id}</span>
                        </li>
                      ))}
                      {(!quote.services || quote.services.length === 0) && (
                        <li className="text-sm text-gray-500">No services</li>
                      )}
                    </ul>
                  </div>

                  {/* Preferred schedule & your notes */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Preferred schedule & your notes
                  </h2>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Preferred schedule</p>
                        <p className="text-sm text-gray-900 font-medium">{formatSchedule(quote.preferredSchedule)}</p>
                      </div>
                      {quote.customerProvidedDetails && (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Your notes</p>
                          <p className="text-sm text-gray-900 font-medium whitespace-pre-wrap">{quote.customerProvidedDetails}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Your uploaded images */}
                  {quote.customerUploadedImages && quote.customerUploadedImages.length > 0 && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                      <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/80">
                        <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide flex items-center gap-2">
                          <Image className="w-4 h-4 text-gray-500" />
                          Your uploaded images
                        </h2>
                      </div>
                      <div className="p-5">
                        {presignedImageUrlsLoading ? (
                          <div className="flex items-center gap-2 text-gray-500 text-sm">
                            <LoadingSpinner size="sm" />
                            Loading images…
                          </div>
                        ) : presignedImageUrls && presignedImageUrls.length > 0 ? (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {presignedImageUrls.map((url, index) => {
                              const loaded = loadedImageIndices.has(index);
                              return (
                                <button
                                  key={index}
                                  type="button"
                                  onClick={() => setLightboxImageUrl(url)}
                                  className="relative block w-full rounded-lg overflow-hidden border border-gray-200 aspect-square bg-gray-100 text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-1"
                                >
                                  {!loaded && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <LoadingSpinner size="sm" />
                                    </div>
                                  )}
                                  <img
                                    src={url}
                                    alt={`Your image ${index + 1}`}
                                    className="w-full h-full object-cover"
                                    onLoad={() => setLoadedImageIndices((prev) => new Set(prev).add(index))}
                                  />
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="text-gray-500 text-sm">Unable to load images.</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Business offer & deposit (read-only) */}
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                    <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Business offer
                      <span
                        className="relative flex-shrink-0"
                        onMouseEnter={() => {
                          if (businessOfferInfoTooltipTimeoutRef.current) {
                            clearTimeout(businessOfferInfoTooltipTimeoutRef.current);
                            businessOfferInfoTooltipTimeoutRef.current = null;
                          }
                          setBusinessOfferInfoTooltipOpen(true);
                        }}
                        onMouseLeave={() => {
                          businessOfferInfoTooltipTimeoutRef.current = setTimeout(() => setBusinessOfferInfoTooltipOpen(false), 150);
                        }}
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-gray-600" aria-label="More information">
                          <i className="bi bi-info-circle text-base" aria-hidden />
                        </span>
                        <span
                          className={`absolute left-1/2 top-full z-10 mt-1.5 -translate-x-1/2 whitespace-normal rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-normal text-gray-600 shadow-lg transition-opacity duration-150 ${businessOfferInfoTooltipOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                          style={{ width: 'max-content', maxWidth: '280px' }}
                          onMouseEnter={() => {
                            if (businessOfferInfoTooltipTimeoutRef.current) {
                              clearTimeout(businessOfferInfoTooltipTimeoutRef.current);
                              businessOfferInfoTooltipTimeoutRef.current = null;
                            }
                            setBusinessOfferInfoTooltipOpen(true);
                          }}
                          onMouseLeave={() => {
                            businessOfferInfoTooltipTimeoutRef.current = setTimeout(() => setBusinessOfferInfoTooltipOpen(false), 150);
                          }}
                        >
                          <span className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-gray-200 bg-white" />
                          {BUSINESS_OFFER_TOOLTIP_TEXT}
                        </span>
                      </span>
                    </h2>
                    <div className="flex flex-wrap gap-x-6 gap-y-3">
                      <div>
                        <p className="text-xs text-gray-500 mb-0.5">Total offer</p>
                        <p className="text-sm font-semibold text-gray-900">{quote.businessOfferAmount != null ? formatMoney(quote.businessOfferAmount) : 'Not yet available.'}</p>
                      </div>
                      {quote.requiredDepositAmount != null && quote.requiredDepositAmount > 0 && (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Deposit required</p>
                          <p className="text-sm text-gray-900 font-medium">{formatMoney(quote.requiredDepositAmount)}</p>
                        </div>
                      )}
                      {quote.offerExpiredAt && (
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Offer expiration</p>
                          <p className="text-sm text-gray-900 font-medium">{isOfferExpired ? `Expired ${formatDate(quote.offerExpiredAt)}` : formatDate(quote.offerExpiredAt)}</p>
                        </div>
                      )}
                    </div>
                    {quote.status === 'BUSINESS_OFFER_SENT' && !isOfferExpired && (
                      <>
                        <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3">
                          <div className="flex items-start gap-2.5 text-sm text-green-800">
                            <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                            <p>
                              {quote.requiredDepositAmount != null && quote.requiredDepositAmount > 0
                                ? 'Once accepted, the business can prepare your appointment. Deposit is required to confirm.'
                                : 'Once accepted, the business can prepare your appointment and move forward with scheduling.'}
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowAcceptConfirm(true)}
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
                          style={{ backgroundColor: '#0B4778' }}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Accept offer
                        </button>
                      </>
                    )}
                    {quote.status === 'BUSINESS_OFFER_SENT' && isOfferExpired && (
                      <p className="mt-4 text-sm text-amber-700">
                        This offer has expired. Please use the conversation below or contact the business directly to request a new offer.
                      </p>
                    )}
                  </div>
                </div>

                {/* Right column — Conversation (desktop) */}
                <div className="lg:min-h-0">
                  <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 lg:sticky lg:top-6">
                    <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <MessageSquare className="w-4 h-4" />
                      Conversation
                      <span
                        className="relative flex-shrink-0"
                        onMouseEnter={() => {
                          if (conversationInfoTooltipTimeoutRef.current) {
                            clearTimeout(conversationInfoTooltipTimeoutRef.current);
                            conversationInfoTooltipTimeoutRef.current = null;
                          }
                          setConversationInfoTooltipOpen(true);
                        }}
                        onMouseLeave={() => {
                          conversationInfoTooltipTimeoutRef.current = setTimeout(() => setConversationInfoTooltipOpen(false), 150);
                        }}
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-100 text-gray-600" aria-label="More information">
                          <i className="bi bi-info-circle text-base" aria-hidden />
                        </span>
                        <span
                          className={`absolute left-1/2 top-full z-10 mt-1.5 -translate-x-1/2 whitespace-normal rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm font-normal text-gray-600 shadow-lg transition-opacity duration-150 ${conversationInfoTooltipOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                          style={{ width: 'max-content', maxWidth: '280px' }}
                          onMouseEnter={() => {
                            if (conversationInfoTooltipTimeoutRef.current) {
                              clearTimeout(conversationInfoTooltipTimeoutRef.current);
                              conversationInfoTooltipTimeoutRef.current = null;
                            }
                            setConversationInfoTooltipOpen(true);
                          }}
                          onMouseLeave={() => {
                            conversationInfoTooltipTimeoutRef.current = setTimeout(() => setConversationInfoTooltipOpen(false), 150);
                          }}
                        >
                          <span className="absolute -top-1.5 left-1/2 h-3 w-3 -translate-x-1/2 rotate-45 border-l border-t border-gray-200 bg-white" />
                          {CONVERSATION_TOOLTIP_TEXT}
                        </span>
                      </span>
                    </h2>
                    <div className="space-y-3 max-h-80 lg:max-h-[calc(100vh-14rem)] overflow-y-auto">
                      {[...(quote.conversations || [])].reverse().map((msg: QuoteConversationMessage) => (
                        <div
                          key={msg.id}
                          className={`p-3 rounded-lg ${msg.sender === 'business' ? 'bg-[#0B4778]/10' : 'bg-gray-100'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <p className="text-sm font-medium text-gray-700">{msg.sender === 'business' ? 'Business' : 'You'}</p>
                            <p className="text-xs text-gray-500 text-right whitespace-nowrap">{formatDate(msg.createdAt)}</p>
                          </div>
                          <p className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{msg.text}</p>
                        </div>
                      ))}
                      {(!quote.conversations || quote.conversations.length === 0) && (
                        <p className="text-sm text-gray-500">No messages yet.</p>
                      )}
                    </div>
                    <div className="pt-3 mt-3 border-t border-gray-100 space-y-3">
                      <textarea
                        placeholder="Reply to business..."
                        value={conversationInput}
                        onChange={(e) => setConversationInput(e.target.value)}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] text-sm"
                      />
                      <button
                        type="button"
                        onClick={handleSendMessage}
                        disabled={!conversationInput.trim() || sendingMessage}
                        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ backgroundColor: '#0B4778' }}
                      >
                        <Send className="w-4 h-4" />
                        {sendingMessage ? 'Sending...' : 'Send'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Accept offer confirmation dialog */}
      {showAcceptConfirm && quote && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" aria-modal="true" role="dialog">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-base font-semibold text-gray-900 mb-2">Accept this offer?</h3>
            <p className="text-gray-600 text-sm mb-4">
              You are accepting the business&apos;s offer of {formatMoney(quote.businessOfferAmount)}
              {quote.requiredDepositAmount != null && quote.requiredDepositAmount > 0 && (
                <> (deposit required: {formatMoney(quote.requiredDepositAmount)})</>
              )}.
              This will confirm the quote with {quote.businessName || 'the business'}.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => !accepting && setShowAcceptConfirm(false)}
                disabled={accepting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAcceptOffer}
                disabled={accepting}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-50"
                style={{ backgroundColor: '#0B4778' }}
              >
                <CheckCircle className="w-4 h-4" />
                {accepting ? 'Accepting…' : 'Accept offer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image lightbox overlay */}
      {lightboxImageUrl && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-4"
          onClick={() => setLightboxImageUrl(null)}
          role="dialog"
          aria-modal="true"
          aria-label="View image"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxImageUrl(null); }}
            className="absolute top-4 right-4 p-2.5 rounded-full bg-white text-gray-900 hover:bg-gray-100 shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-black/80"
            aria-label="Close"
          >
            <X className="w-6 h-6" strokeWidth={2.5} />
          </button>
          <img
            src={lightboxImageUrl}
            alt="Enlarged view"
            className="max-w-full max-h-[90vh] w-auto h-auto object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      <Footer />
    </div>
  );
};

export default CustomerQuoteRequestDetailsPage;
